import TransactionsI from '../interfaces/Transactions';
import { useEffect, useState } from 'react';
import Statistics from '../interfaces/Statistics';
import isRealPaidEntryFeeTx from '../functions/isRealPaidEntryFeeTx';

const PURCHASE_FEES = new Set([
    'VIP Band',
    'Jukebox',
    'Water',
    'Monster',
    'Red Bull',
]);

const sumQty = (txs: any[], feeName: string): number =>
    txs
        .filter((t) => t.fee === feeName)
        .reduce((acc, t) => acc + (Number(t.quantity) || 1), 0);

const useStatistics = (transactions: TransactionsI) => {
    const [statistics, setStatistics] = useState<Statistics | null>(null);

    useEffect(() => {
        if (!transactions) return;
        const allAccess = transactions.allAccess || [];
        const limited = transactions.limited || [];
        const all = [...allAccess, ...limited];

        // Unique all-access guests = unique guestID (or stageName as fallback)
        // among allAccess transactions
        const allAccessGuestIds = new Set(
            allAccess.map((t: any) => t.guestID || t.stageName)
        );

        // Limited-access guests = limited check-ins that are NOT kiosk sales.
        // Kiosk sales use guestID='SALE'; real check-ins have a unique guest id.
        const limitedCheckIns = limited.filter(
            (t: any) =>
                t.guestID !== 'SALE' && !PURCHASE_FEES.has(t.fee || '')
        );
        const limitedGuestIds = new Set(
            limitedCheckIns.map((t: any) => t.guestID || t.stageName)
        );

        // Fee totals — only count real paid AAEventFee transactions.
        // SSO presence stubs ($0, no source) and voided transactions
        // are excluded so the totals reflect actual revenue collected.
        const allAccessFees = allAccess.reduce(
            (acc: number, t: any) =>
                isRealPaidEntryFeeTx(t)
                    ? acc + (Number(t.value) || 0)
                    : t.fee !== 'AAEventFee' && t.voided !== true
                    ? acc + (Number(t.value) || 0)  // VIP Band / Jukebox / etc.
                    : acc,
            0
        );
        const limitedAccessFees = limited.reduce(
            (acc: number, t: any) =>
                t.voided === true ? acc : acc + (Number(t.value) || 0),
            0
        );

        setStatistics({
            allAccessGuests: allAccessGuestIds.size,
            limitedAccessGuests: limitedGuestIds.size,
            allAccessFees,
            limitedAccessFees,
            totalFees: allAccessFees + limitedAccessFees,
            vipBandSold: sumQty(all, 'VIP Band'),
            jukeboxSold: sumQty(all, 'Jukebox'),
            waterSold: sumQty(all, 'Water'),
            monsterSold: sumQty(all, 'Monster'),
            redBullSold: sumQty(all, 'Red Bull'),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { statistics, setStatistics };
};

export default useStatistics;
